/**
 * @component CompanyCustomersPage
 * @description 새힘정보기술의 주요 고객사 로고를 제약·화장품·식품·기타 부문별 탭으로 소개하는 페이지입니다.
 * @usage App 라우팅에서 `/company/customers.html` 경로로 렌더링됩니다.
 * @note 유지보수 고객사는 별도 CompanyMaintenancePage(/company/maintenance.html)로 분리되었습니다.
 */

import { useState } from 'react';
import PageHero from '../../components/common/PageHero';
import SectionHeading from '../../components/common/SectionHeading';
import { pageMeta } from '../../constants/navigation';

// 제약 부문 고객사 로고
import naturetecImg from '../../customers/naturetec.jpg';
import daewonpharmImg from '../../customers/daewonpharm.png';
import daihanImg from '../../customers/daihan.png';
import daewoongbioImg from '../../customers/daewoongbio.png';
import ilDongImg from '../../customers/ILDong.png';
import samjinImg from '../../customers/samjin.png';
import whaninImg from '../../customers/whanin.png';
import taigukImg from '../../customers/taiguk.png';
import genuonesciencesImg from '../../customers/genuonesciences.png';
import samilImg from '../../customers/samil.png';
import yungjinImg from '../../customers/yungjin.png';
import daehwaImg from '../../customers/Daehwa.png';
import curocellbtxImg from '../../customers/curocellbtx.svg';
import bukwangImg from '../../customers/bukwang.png';
import dalimpharmImg from '../../customers/dalimpharm.png';
import shinpoongImg from '../../customers/shinpoong.png';
import firsonImg from '../../customers/firson.png';
import ckdbioImg from '../../customers/ckdbio.png';
import koreaprimeImg from '../../customers/koreaprime.gif';
import arlicoImg from '../../customers/arlico.png';
import mmpharmImg from '../../customers/mmpharm.png';
import kuhnilImg from '../../customers/kuhnil.png';
import otsukaImg from '../../customers/Otsuka.png';
import ajupharmImg from '../../customers/ajupharm.png';
import dkpharmImg from '../../customers/dkpharm_2.png';
import suheunghealthcareImg from '../../customers/suheunghealthcare.png';
import reyonpharmImg from '../../customers/reyonpharm.png';
import kwangdongImg from '../../customers/kwangdong.png';
import hanallImg from '../../customers/hanall.png';
import cosmaxbioImg from '../../customers/cosmaxbio.svg';
import cosmaxnbtImg from '../../customers/cosmaxnbt.png';
import dongHwaImg from '../../customers/dongHwa.png';
import celltrionImg from '../../customers/celltrion.png';
import aekyungImg from '../../customers/aekyung.png';

// 화장품 부문 고객사 로고
import lccCareImg from '../../customers/lcccare.png';
import sewhaImg from '../../customers/sewha.svg';
import cosmaxImg from '../../customers/cosmax.png';
import cosmeccaImg from '../../customers/cosmecca.png';
import neopharmImg from '../../customers/neopharm.jpg';
import pharmaresearchImg from '../../customers/pharmaresearch.svg';
import englewoodlabImg from '../../customers/englewoodlab.jpg';
import genicImg from '../../customers/genic.png';
import coreanaImg from '../../customers/coreana.png';
import saimdangImg from '../../customers/saimdang.png';
import welcosImg from '../../customers/welcos.png';

// 식품 부문 고객사 로고
import iikimImg from '../../customers/iikim.png';


const THIS_YEAR = new Date().getFullYear();

// [비즈니스] 기타 부문 납품 실적 — GMP 산업 외 분야의 계약 이력을 연도순으로 관리합니다.
// [비즈니스] 사용기간은 현재 연도 기준으로 자동 계산되므로 introYear만 관리합니다.

const OTHER_TRACK_RECORD = [
  { id: 'englewoodlab-other', name: '잉글우드랩코리아',          introYear: 2025, system: '제조실행관리시스템(MES)' },
  { id: 'woongjin',           name: '웅진(다림바이오텍)',        introYear: 2023, system: '제조실행관리시스템(MES)' },
  { id: 'bukwang-other',      name: '부광약품',                 introYear: 2023, system: '칭량관리시스템(RWS) + 창고관리' },
  { id: 'yungjin-other',      name: '영진약품',                 introYear: 2023, system: '제조실행관리시스템(MES)' },
  { id: 'cosmax-id',          name: '코스맥스(인도네시아)',      introYear: 2023, system: '칭량관리시스템(RWS)' },
  { id: 'daewonpharm-other',  name: '대원제약',                 introYear: 2022, system: '칭량관리시스템(RWS) + 창고관리' },
  { id: 'curocell-other',     name: '큐로셀',                   introYear: 2022, system: '추적관리시스템(Traceability)' },
  { id: 'genuone-other',      name: '제뉴원사이언스',            introYear: 2022, system: '제조실행관리시스템(MES)' },
  { id: 'bell-ins',           name: '벨아이앤에스(종근당)',      introYear: 2021, system: '제조실행관리시스템(MES)' },
  { id: 'mmpharm-other',      name: '명문제약',                 introYear: 2021, system: '창고관리시스템(WMS)' },
  { id: 'arlico-other',       name: '알리코제약',               introYear: 2020, system: '창고관리시스템(WMS)' },
  { id: 'firson-other',       name: '퍼슨제약',                 introYear: 2020, system: '창고관리시스템(WMS)' },
  { id: 'cosmecca-other',     name: '코스메카코리아',            introYear: 2020, system: '제조실행관리시스템(MES)' },
  { id: 'cosmaxbio-jecheon',  name: '코스맥스바이오(한국-제천)', introYear: 2020, system: '창고관리시스템(WMS)' },
  { id: 'cosmaxnbt-icheon',   name: '코스맥스NBT(한국-이천)',    introYear: 2020, system: '창고관리시스템(WMS)' },
  { id: 'kuhnil-other',       name: '건일제약',                 introYear: 2019, system: '칭량관리시스템(RWS)' },
  { id: 'fmg',                name: '에프엠지',                 introYear: 2019, system: '창고관리시스템(WMS) + 제조실행관리시스템(MES)' },
  { id: 'cosmaxnbt-au',       name: '코스맥스NBT(호주)',         introYear: 2019, system: '창고관리시스템(WMS)' },
  { id: 'ajupharm-other',     name: '아주약품',                 introYear: 2016, system: '창고관리시스템(WMS)' },
  { id: 'cosmax-kr',          name: '코스맥스(한국-향남/평택)',  introYear: 2016, system: '창고관리시스템(WMS)' },
  { id: 'kwangdong',          name: '광동제약',                 introYear: 2015, system: '제조실행관리시스템(MES)' },
  { id: 'otsuka-hyannam',     name: '한국오츠카제약_향남',       introYear: 2015, system: '칭량관리시스템(RWS)' },
  { id: 'hanolbio',           name: '한올바이오파마',            introYear: 2014, system: '자재/창고관리시스템(MWS)' },
  { id: 'cosmax-us',          name: '코스맥스(미국)',            introYear: 2014, system: '칭량관리시스템(RWS)' },
  { id: 'shinpoong',          name: '신풍제약',                 introYear: 2013, system: '제조실행관리시스템(MES)' },
  { id: 'cmjc',               name: '씨엠제이씨(구.일동제약)',   introYear: 2013, system: '칭량관리시스템(RWS)' },
  { id: 'donghwa',            name: '동화약품',                 introYear: 2013, system: '칭량관리시스템(RWS)' },
  { id: 'genic-other',        name: '제닉',                     introYear: 2013, system: '칭량관리시스템(RWS)' },
  { id: 'daehwa-other',       name: '대화제약',                 introYear: 2013, system: '제조실행관리시스템(MES)' },
];

// [비즈니스] 고객사 목록은 계약 관계 및 레퍼런스 공개 동의 기준으로 부문별 관리됩니다.
const CUSTOMER_GROUPS = {
  pharma: [
    { id: 'naturetec',        name: '네이처텍',      img: naturetecImg },
    { id: 'daewonpharm',      name: '대원제약',      img: daewonpharmImg },
    { id: 'dongHwa',          name: '동화약품',      img: dongHwaImg },
    { id: 'daihan',           name: '대한약품공업',  img: daihanImg },
    { id: 'daewoongbio',      name: '대웅바이오',    img: daewoongbioImg },
    { id: 'ilDong',           name: '일동제약',      img: ilDongImg },
    { id: 'kwangdong',        name: '광동제약',      img: kwangdongImg },
    { id: 'samjin',           name: '삼진제약',      img: samjinImg },
    { id: 'whanin',           name: '환인제약',      img: whaninImg },
    { id: 'taiguk',           name: '태극제약',      img: taigukImg },
    { id: 'genuonesciences',  name: '제뉴원사이언스', img: genuonesciencesImg },
    { id: 'samil',            name: '삼일제약',      img: samilImg },
    { id: 'yungjin',          name: '영진약품',      img: yungjinImg },
    { id: 'daehwa',           name: '대화제약',      img: daehwaImg },
    { id: 'hanall',           name: '한올바이오파마',      img: hanallImg },
    { id: 'curocellbtx',      name: '큐로셀',        img: curocellbtxImg },
    { id: 'cosmaxbio',        name: '코스맥스바이오', img: cosmaxbioImg },
    { id: 'cosmaxnbt',        name: '코스맥스NBT', img: cosmaxnbtImg },
    { id: 'bukwang',          name: '부광약품',      img: bukwangImg },
    { id: 'dalimpharm',       name: '다림바이오텍',  img: dalimpharmImg },
    { id: 'shinpoong',        name: '신풍제약',      img: shinpoongImg },
    { id: 'firson',           name: '퍼슨제약',      img: firsonImg },
    { id: 'ckdbio',           name: '종근당바이오',  img: ckdbioImg },
    { id: 'koreaprime',       name: '한국프라임제약', img: koreaprimeImg },
    { id: 'arlico',           name: '알리코제약',    img: arlicoImg },
    { id: 'mmpharm',          name: '명문제약',      img: mmpharmImg },
    { id: 'kuhnil',           name: '건일제약',      img: kuhnilImg },
    { id: 'otsuka',           name: '한국오츠카',    img: otsukaImg },
    { id: 'ajupharm',         name: '아주약품',      img: ajupharmImg },
    { id: 'dkpharm',          name: '동국제약',       img: dkpharmImg },
    { id: 'suheunghealthcare', name: '서흥',          img: suheunghealthcareImg },
    { id: 'reyonpharm',       name: '이연제약',       img: reyonpharmImg },
    { id: 'celltrion',        name: '셀트리온',       img: celltrionImg },
  ],
  cosmetics: [
    { id: 'lccCare',        name: 'LCC',            img: lccCareImg },
    { id: 'sewha',          name: '세화피앤씨',     img: sewhaImg },
    { id: 'cosmax',         name: '코스맥스',       img: cosmaxImg },
    { id: 'cosmecca',       name: '코스메카코리아', img: cosmeccaImg },
    { id: 'neopharm',       name: '네오팜',         img: neopharmImg },
    { id: 'pharmaresearch', name: '파마리서치',     img: pharmaresearchImg },
    { id: 'englewoodlab',   name: '잉글우드랩코리아', img: englewoodlabImg },
    { id: 'genic',          name: '제닉',           img: genicImg },
    { id: 'coreana',        name: '코리아나',       img: coreanaImg },
    { id: 'saimdang',       name: '사임당',         img: saimdangImg },
    { id: 'welcos',         name: '웰코스',         img: welcosImg },
    { id: 'aekyung',        name: '애경산업',       img: aekyungImg },
  ],
  food: [
    { id: 'iikim',          name: '이킴',        img: iikimImg },
  ],
  other: [
  ],
};

const TABS = [
  { id: 'pharma',    label: '제약' },
  { id: 'cosmetics', label: '화장품' },
  { id: 'food',      label: '식품' },
  { id: 'other',     label: '도입이력' },
];

export default function CompanyCustomersPage({ onNavigate }) {
  const [activeTab, setActiveTab] = useState('pharma');
  const displayedCustomers = CUSTOMER_GROUPS[activeTab];

  return (
    <>
      <PageHero
        title={pageMeta.companyCustomers.title}
        trail={pageMeta.companyCustomers.trail}
        className="company-page-hero"
      />
      <section className="section">
        <div className="container">
          <SectionHeading
            eyebrow="Customers"
            title="함께 성장하는 파트너사"
            description="제약·화장품 분야 선도 기업들과 함께 스마트 제조 혁신을 이끌어왔습니다."
            className="fade-in"
          />

          {/* 부문별 내부 탭 — solution-tabs(박스형)와 구별되는 언더라인 방식 */}
          <div className="customers-tabs" role="tablist" aria-label="고객사 부문 탭">
            {TABS.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={activeTab === id}
                className={`customers-tab-btn${activeTab === id ? ' is-active' : ''}`}
                onClick={() => setActiveTab(id)}
              >
                {label}
                <span className="customers-tab-count">
                  {id === 'other' ? OTHER_TRACK_RECORD.length : CUSTOMER_GROUPS[id].length}
                </span>
              </button>
            ))}
          </div>

          {activeTab === 'other' ? (
            /* [비즈니스] 기타 부문은 로고 대신 납품 이력 리스트로 표시해 다양한 산업 레퍼런스를 전달합니다. */
            <>
              <p className="customers-list-desc">
                ㈜새힘정보기술은 GMP 산업 외 다양한 산업 분야에서 제조실행관리(MES), 창고관리(WMS), 칭량관리(RWS) 시스템 및 맞춤형 솔루션을 공급한 실적을 보유하고 있습니다.
              </p>
              <div className="customers-list-wrap">
                <table className="customers-list-table">
                  <thead>
                    <tr>
                      <th>번호</th>
                      <th>고객사명</th>
                      <th>도입연도</th>
                      <th>사용기간</th>
                      <th>솔루션</th>
                    </tr>
                  </thead>
                  <tbody>
                    {OTHER_TRACK_RECORD.map(({ id, name, introYear, system }, index) => (
                      <tr key={id}>
                        <td>{index + 1}</td>
                        <td>{name}</td>
                        <td>{introYear}년</td>
                        <td>{THIS_YEAR - introYear}년</td>
                        <td>{system}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            /* [비즈니스] 고객사 로고 노출로 신뢰도와 레퍼런스를 직관적으로 전달합니다. */
            <ul className="customers-grid">
              {displayedCustomers.map(({ id, name, img }) => (
                <li key={id} className="customers-card">
                  <img src={img} alt={name} className="customers-logo" />
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </>
  );
}
